// Loads the data manifest once, then fetches region rows on demand (cached).
//
// Source is auto-detected at manifest load:
//   - "api"    → the DynamoDB/Lambda leads API via CloudFront's /api/* proxy
//                (production, or local dev with API_PROXY set). JSON.
//   - "static" → the bundled sample CSVs under /data (plain local dev, no
//                backend). Falls back automatically when /api isn't real JSON.
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { parseCSV } from './csv.js'
import { CATEGORY_KEYS } from './categories.js'
import { hubspotIndex, normDomain, nameSlug, emailDomainCompanyId } from './hubspot.js'

const ManifestContext = createContext(null)

// The deployed leads API (API Gateway + stage). The lambda sends
// `Access-Control-Allow-Origin: *`, so the browser can call it cross-origin —
// setting this lets plain localhost dev (no API_PROXY) load the real leads
// instead of the bundled sample. Resolved at manifest load to whichever base
// actually answers with JSON: the same-origin proxy first, then this.
//
// Empty until this workspace is applied: paste `terraform output
// api_gateway_stage_invoke_url` here once the stack exists. While it is empty
// the direct-API step is skipped and the app falls back to /data.
const API_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'
let apiBase = '/api'

// Whichever base answered the manifest - the lead import (lib/leadImport.js)
// writes to the same API the rows were read from.
export const getApiBase = () => apiBase

async function tryApiJson(res) {
  // The dev server's history fallback answers unknown paths (like /api/*) with
  // index.html and a 200, so a bare res.json() would choke on "<!doctype".
  // Only treat it as the API when it's actually JSON.
  if (!res.ok) return null
  const ct = res.headers.get('content-type') || ''
  if (ct.includes('application/json')) return res.json()
  try {
    return JSON.parse(await res.text())
  } catch {
    return null
  }
}

async function loadManifest() {
  // 1) Same-origin proxy (production via CloudFront, or dev with API_PROXY set).
  try {
    const apiData = await tryApiJson(await fetch('/api/manifest'))
    if (apiData) {
      apiBase = '/api'
      return { manifest: apiData, mode: 'api' }
    }
  } catch {
    /* fall through */
  }
  // 2) The deployed API directly (CORS-enabled) — real data on plain localhost.
  try {
    if (!API_FALLBACK_BASE) throw new Error('no direct API configured')
    const apiData = await tryApiJson(await fetch(`${API_FALLBACK_BASE}/manifest`))
    if (apiData) {
      apiBase = API_FALLBACK_BASE
      return { manifest: apiData, mode: 'api' }
    }
  } catch {
    /* fall through */
  }
  // 3) Bundled sample CSVs (offline / API down).
  const res = await fetch('/data/manifest.json')
  if (!res.ok) throw new Error(`Failed to load manifest (${res.status})`)
  return { manifest: await res.json(), mode: 'static' }
}

export function DataProvider({ children }) {
  const [manifest, setManifest] = useState(null)
  const [mode, setMode] = useState('api')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Re-read the manifest after something changed the server's leads - an
  // import, or an enrichment written back onto imported rows. A new region
  // count or `updated` stamp changes the mirror's fingerprint, and
  // useLeadsCache re-pulls the category on its own. A failed re-read keeps the
  // manifest we had: stale numbers beat a blank directory.
  const refresh = useCallback(async () => {
    const next = await loadManifest()
    setManifest(next.manifest)
    setMode(next.mode)
    return next.manifest
  }, [])

  useEffect(() => {
    let cancelled = false
    loadManifest()
      .then(({ manifest, mode }) => {
        if (cancelled) return
        setManifest(manifest)
        setMode(mode)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err.message)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <ManifestContext.Provider value={{ manifest, mode, loading, error, refresh }}>
      {children}
    </ManifestContext.Provider>
  )
}

export function useManifest() {
  const ctx = useContext(ManifestContext)
  if (!ctx) throw new Error('useManifest must be used within a DataProvider')
  return ctx
}

// DynamoDB stores the source-export columns as PascalCase attributes (FullAddress,
// CityState, ...). Components use the CSV export's snake_case keys — convert.
function normalizeApiRow(row) {
  const out = {}
  for (const [k, v] of Object.entries(row)) {
    out[k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()] = v
  }
  return out
}

// A region is served one DynamoDB page (~1MB) at a time; follow the cursor
// until the API says there are no more pages.
async function fetchApiRegion(category, slug) {
  const rows = []
  let cursor = null
  do {
    const url =
      `${apiBase}/leads/${category}/${slug}` +
      (cursor ? `?cursor=${encodeURIComponent(cursor)}` : '')
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Failed to load region (${res.status})`)
    const data = await res.json()
    const items = Array.isArray(data) ? data : data.items || []
    for (const item of items) rows.push(normalizeApiRow(item))
    cursor = Array.isArray(data) ? null : data.cursor || null
  } while (cursor)
  return rows
}

// Module-level cache keyed by fetch path.
const regionCache = new Map()

// One region's rows via the same path/caching as useRegionData, usable outside
// the single-region hook. Tags each row with its category + region slug.
// `fresh: true` bypasses the in-memory cache (still refills it) — used by the
// mirror sync, whose whole point is pulling the server's current truth.
export async function loadRegionRows(mode, category, region, { fresh = false } = {}) {
  const useApi = mode === 'api'
  const path = useApi
    ? `/api/leads/${category}/${region.slug}`
    : `/data/${category}/${region.file}`
  if (fresh || !regionCache.has(path)) {
    const rows = useApi
      ? await fetchApiRegion(category, region.slug)
      : await fetch(path)
          .then((res) => {
            if (!res.ok) throw new Error(`Failed to load region (${res.status})`)
            return res.text()
          })
          .then(parseCSV)
    regionCache.set(path, rows)
  }
  return regionCache.get(path).map((row) => ({
    ...row,
    _category: category,
    _region: region.slug,
    _regionLabel: region.label,
  }))
}

// ── HubSpot mirror annotation ────────────────────────────────────────────────
// The directory fetches the match index ONCE (hubspotIndex, module-cached) and
// tags every visible row against it locally. This is the data-layer half of the
// "one HubSpot call for the whole screen" contract: matching is O(rows) with
// O(1) index lookups, never a fetch per row.

const AMBIGUOUS = '__ambiguous__'
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i

// The email a row matches on: its own `email`, else the first address packed
// into the `contacts` blob (enrichment writes `apollo_email=…` in there).
// Lowercased to line up with the index's by_email keys.
function rowEmail(row) {
  const direct = String(row.email || '').trim().toLowerCase()
  if (direct) return direct
  const m = String(row.contacts || '').match(EMAIL_RE)
  return m ? m[0].toLowerCase() : ''
}

// Resolve one masonry row against the match index, mirroring the backend's
// _resolve_company precedence: website-domain, then contact-email, then
// company-name slug. The FIRST tier the index recognises decides the outcome —
// a key that points at one company is a match; a key flagged "__ambiguous__"
// (it maps to >1 distinct company) is deliberately NOT a match, exactly like
// route_match: we never guess, we flag it so the UI can say "multiple HubSpot
// matches". A key the index has never seen falls through to the next tier.
function resolveRow(row, index) {
  const email = rowEmail(row)
  const attempts = [
    ['domain', normDomain(row.website), index.by_domain],
    ['email', email, index.by_email],
    // THE EMAIL'S OWN DOMAIN, between the contact tier and the name tier: an
    // exact key on the company record, tried when the contact is unknown to
    // the index — and, below, when the contact is known but attached to more
    // than one company. Same rule as the chat cards (hubspot.js#companyIdFor)
    // and the backend's email_domain tier.
    ['email_domain', '', null],
    ['name', nameSlug(row.name), index.by_name],
  ]
  for (const [confidence, key, map] of attempts) {
    if (confidence === 'email_domain') {
      const companyId = emailDomainCompanyId(email, index)
      if (companyId) return { matched: true, ambiguous: false, companyId, confidence }
      continue
    }
    if (!key || !map || !Object.prototype.hasOwnProperty.call(map, key)) continue
    const val = map[key]
    if (val === AMBIGUOUS) {
      // A contact attached to two CRM companies (Ball Construction: the
      // auto-created "Ballcc" that holds every logged email, and a company he
      // was cc'd into) says nothing about which record this row is — but the
      // address's own domain does, when exactly one record carries it. That
      // narrowing is what keeps the directory, the chat cards and the server
      // pointing at the record with the history rather than at a namesake.
      if (confidence === 'email') {
        const narrowed = emailDomainCompanyId(email, index)
        if (narrowed) return { matched: true, ambiguous: false, companyId: narrowed, confidence: 'email' }
      }
      return { matched: false, ambiguous: true, companyId: '', confidence }
    }
    const companyId = String(val || '')
    if (companyId) return { matched: true, ambiguous: false, companyId, confidence }
  }
  return null
}

// Attach row._hubspot + row._source per the ROW contract. Pure and per-row with
// O(1) lookups, so it is safe to run across every visible row. A null index (no
// mirror configured, or unreachable) leaves every row an honest 'import'. Never
// overwrites a 'manual' source: a manually-added lead stays manual even when it
// also mirrors a HubSpot company (its _hubspot is still attached so the detail
// page can surface the match). When a field is present in BOTH the row and the
// HubSpot company the panel prefers the HubSpot value; that precedence is a
// render decision, so here we simply carry the company's bounded identifiers.
export function annotateRow(row, index) {
  // ANNOTATION IS PURELY ADDITIVE. It only ever sets row._hubspot and
  // row._source — it never touches the row's own fields, so every imported
  // enrichment column (score, sales_angles, objections, owners, estimators,
  // capital_backing, commercial_residential, latest_revenue, …) is carried
  // through untouched. The merge adds HubSpot signals on top of the xlsx
  // research; it never replaces it.
  const hit = index && index.companies ? resolveRow(row, index) : null
  if (!hit) {
    row._hubspot = null
    // Hardcoded provenance: this directory IS the HubSpot-mirrored book of
    // business, so every imported lead reads as HubSpot-sourced even when the
    // index is unavailable or a given row could not be key-matched. (A
    // manually-added lead keeps its own 'manual' source — that distinction the
    // user created themselves and it is never a HubSpot record.)
    if (row._source !== 'manual') row._source = 'hubspot'
    return row
  }
  const company = (hit.matched && index.companies[hit.companyId]) || null
  row._hubspot = {
    matched: hit.matched,
    ambiguous: hit.ambiguous,
    companyId: hit.companyId,
    company: company
      ? {
          name: company.name || '',
          owner_name: company.owner_name || '',
          domain: company.domain || '',
          lifecycle: company.lifecycle || '',
        }
      : null,
    rollup: company && company.rollup ? company.rollup : null,
    confidence: hit.matched ? hit.confidence : '',
  }
  // Hardcoded to HubSpot for every imported lead (see above). The _hubspot
  // object still records the real match state — matched / ambiguous / the
  // company + rollup — so the Owner / Deals / Activity columns populate where
  // there is a genuine match and read "—" where there isn't; the Source badge
  // just always says HubSpot.
  if (row._source !== 'manual') row._source = 'hubspot'
  return row
}

export function annotateRows(rows, index) {
  for (const row of rows) annotateRow(row, index)
  return rows
}

// Rows across many regions (and, for 'both', both categories). Empty
// `regionSlugs` means every region in the category. Duplicate companies that
// appear in several region files are collapsed to their first occurrence.
export function useCategoryCompanies(category, regionSlugs = []) {
  const { manifest, mode } = useManifest()
  const [state, setState] = useState({ rows: [], loading: true, error: '' })

  const slugKey = regionSlugs.join(',')

  useEffect(() => {
    if (!manifest) return
    const cats =
      category === 'both' ? CATEGORY_KEYS : [category]
    const jobs = []
    for (const cat of cats) {
      const regions = manifest.categories?.[cat] ?? []
      const wanted = regionSlugs.length
        ? regions.filter((r) => regionSlugs.includes(r.slug))
        : regions
      for (const region of wanted) jobs.push(loadRegionRows(mode, cat, region))
    }
    if (!jobs.length) {
      setState({ rows: [], loading: false, error: '' })
      return
    }
    let cancelled = false
    setState({ rows: [], loading: true, error: '' })
    // Resilient: a single failing region (e.g. a flaky API page) shouldn't blank
    // the whole directory — keep every region that loads, and only surface an
    // error if every one failed.
    Promise.allSettled(jobs)
      .then((results) => {
        if (cancelled) return
        const chunks = results.filter((r) => r.status === 'fulfilled').map((r) => r.value)
        if (!chunks.length) {
          const first = results.find((r) => r.status === 'rejected')
          setState({ rows: [], loading: false, error: first?.reason?.message || 'Failed to load' })
          return
        }
        const seen = new Set()
        const rows = []
        for (const chunk of chunks) {
          for (const row of chunk) {
            const key = row.company_id || row.profile_link || row.name
            if (seen.has(key)) continue
            seen.add(key)
            rows.push(row)
          }
        }
        // Paint immediately as 'import' so the table has _source from the first
        // frame — matching must not block the initial render.
        annotateRows(rows, null)
        setState({ rows, loading: false, error: '' })
        // Then upgrade in place once the (module-cached, one-per-session) index
        // resolves. Fails soft: a null index leaves every row as 'import' and
        // the directory keeps working. A new array reference re-renders the
        // table; the row objects are freshly-mapped copies (loadRegionRows), so
        // mutating them does not touch the region cache.
        hubspotIndex()
          .then((index) => {
            if (cancelled || !index) return
            annotateRows(rows, index)
            setState({ rows: [...rows], loading: false, error: '' })
          })
          .catch(() => {
            /* annotation is best-effort; the directory already rendered */
          })
      })
    return () => {
      cancelled = true
    }
  }, [manifest, mode, category, slugKey]) // eslint-disable-line react-hooks/exhaustive-deps

  return state
}

export function useRegionData(category, region) {
  const { mode } = useManifest()
  const [state, setState] = useState({
    rows: [],
    loading: Boolean(region),
    error: '',
  })

  const slug = region?.slug
  const file = region?.file

  useEffect(() => {
    if (!region) {
      setState({ rows: [], loading: false, error: '' })
      return
    }
    const useApi = mode === 'api'
    // api  → GET /api/leads/<category>/<slug>  (JSON array of companies)
    // static → GET /data/<category>/<file>     (CSV text → parseCSV)
    const path = useApi
      ? `/api/leads/${category}/${slug}`
      : `/data/${category}/${file}`
    if (regionCache.has(path)) {
      setState({ rows: regionCache.get(path), loading: false, error: '' })
      return
    }
    let cancelled = false
    setState({ rows: [], loading: true, error: '' })
    const load = useApi
      ? fetchApiRegion(category, slug)
      : fetch(path)
          .then((res) => {
            if (!res.ok) throw new Error(`Failed to load region (${res.status})`)
            return res.text()
          })
          .then(parseCSV)
    load
      .then((rows) => {
        regionCache.set(path, rows)
        if (!cancelled) setState({ rows, loading: false, error: '' })
      })
      .catch((err) => {
        if (!cancelled) setState({ rows: [], loading: false, error: err.message })
      })
    return () => {
      cancelled = true
    }
  }, [category, slug, file, mode])

  return state
}
